// # Main routing configuration
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import ProtectedRouteAdmin from './protected/protected_route_admin';
import ProtectedRouteUser from './protected/protected_route_user';
import ScrollToTop from './route_handles/ScrollToTop';
// # Import auth store
import { useAuthStore } from '../auth/state';

// # Import public routes
import publicRoutes from './public_routes';

// # Import route components
import AdminDashRoutes from './admin_routes/admin_dash_routes';
import AdminSecuredRoutes from './admin_routes/admin_secured_routes';
import AdminLeaderboards from '../roles/admin/admin_tools/leaderboards/admin_leaderboards';
import UserDashRoutes from './user_routes/user_dash_routes';
import GeneralRoutes from './more_routes/general_routes';
import SignupRoutes from './more_routes/auth_routes';
import MenteeGuide from '../neo_mentorship/common/mentee_guide';
// # Import employee routes
import TutorRoutes from './employee_routes/tutor_routes';
import ContentReviewerRoutes from './employee_routes/content_reviewer_routes';
import DesignerRoutes from './employee_routes/designer_routes';
import FinancialManagerRoutes from './employee_routes/financial_manager_routes';
import SupportRoutes from './employee_routes/support_routes';
// # Import mentor routes
import MentorRoutes from './visitor_routes/mentor_routes';

// # Import landing pages
import Landing from '../landing/landing';
import Products from '../landing/pages/products';
// import Team from '../landing/pages/team';
import Samples from '../landing/pages/samples';
import Signin from '../landing/pages/signin';
import Terms from '../landing/pages/terms';
import Privacy from '../landing/pages/privacy';
import FAQs from '../landing/pages/faqs';
import Reviews from '../landing/pages/review';
import Welcome from '../landing/pages/signin/welcome';
// # Import maintenance mock pages
import SigninMock from '../landing/mocks/signin_mock';
import SignupMock from '../landing/mocks/signup_mock';

// # Import dashboard components
import UserDashboard from '../roles/user/user_dashboard';
import AdminDashboard from '../roles/admin/admin_dashboard';

// # Import employee dashboard components
import TutorDashboard from '../roles/employees/tutor/tutor_dashboard';
import ContentReviewerDashboard from '../roles/employees/content_reviewer/cont_rev_dashboard';
import DesignerDashboard from '../roles/employees/designer/designer_dashboard';
import FinancialManagerDashboard from '../roles/employees/financial_manager/fin_man_dashboard';
import SupportDashboard from '../roles/employees/support/support_dashboard';

// # Import profile and settings
import Profile from '../general/profile/profile';
import Settings from '../general/gen_pages/settings';

// # Import library components
import LibraryStep1 from '../general/library/step1/library_step_1';
import LibraryStep2 from '../general/library/step2/library_step_2';
import ChapterIframe from '../general/library/chapter_iframe';
import Step1Chapter from '../general/library/step1/step1_chapter';
import ChapterStats from '../general/library/step1/chapter_stats1';
import Step2Chapter from '../general/library/step2/step2_chapter';
import ChapterStats2 from '../general/library/step2/chapter_stats2';

// # Import notes and bookmarks components
import MyNotes from '../general/notes/my_notes';
import MyBookmarks from '../general/bookmarks/my_bookmarks';

// # Import user feedback components
import UserFeedback from '../systems/feedbacks/feedback_user';
import AdminFeedback from '../systems/feedbacks/feedback_admin';

// # Import community components
import TakeNotes from '../general/extra_content/take_notes';
import Experiences from '../general/extra_content/experiences/experiences';
import TakeBreak from '../general/extra_content/take_break/take_break';
import Roadmaps from '../general/extra_content/roadmaps/roadmaps';
import Updates from '../general/extra_content/updates/updates';

// # Import extras components
import Gallery from '../general/extra_content/gallery/gallery';
import Clinical from '../general/extra_content/clinical/clinical';
import Research from '../general/extra_content/research/research';
import OET from '../general/extra_content/oet/oet';

// # Import financial components
import SalaryManagement from '../roles/admin/admin_tools/finance/salaries/salary';
import ExpensesTracking from '../roles/admin/admin_tools/finance/expenses/expenses';
import RevenueAnalytics from '../roles/admin/admin_tools/finance/revenues/net_revenue';
import PaymentProcessing from '../roles/admin/admin_tools/finance/payments/payments';

// # Import analytics components
import GrowthAnalytics from '../roles/admin/admin_tools/analytics/growth_analytics';
import ActivityAnalytics from '../roles/admin/admin_tools/analytics/activity_analytics';
import HeatMap from '../roles/admin/admin_tools/analytics/heat_map';
import SubscriptionAnalytics from '../roles/admin/admin_tools/analytics/subscription_analytics';
import TechnicalAnalytics from '../roles/admin/admin_tools/analytics/technical_analytics';
import AuthAnalytics from '../roles/admin/admin_tools/analytics/auth_analytics';

// # Import labs component
import Labs from '../general/extra_content/labs/labs';

// # Import mentorship components
import NeoMenteeStep1 from '../neo_mentorship/neo_m_step1/neo_mentee_step1';
import NeoMenteeStep2 from '../neo_mentorship/neo_m_step2/neo_mentee_step2';
import NeoTime from '../neo_mentorship/common/neo_time';
import M1Calendar from '../neo_mentorship/neo_m_step1/mentoring1/m1_calendar';
import M2Calendar from '../neo_mentorship/neo_m_step2/mentoring2/m2_calendar';
import Counter1 from '../neo_mentorship/neo_m_step1/mentoring1/counter1';
import Counter1Page from '../neo_mentorship/neo_m_step1/mentoring1/counter1_page';
import Counter2 from '../neo_mentorship/neo_m_step2/mentoring2/counter2';
import Counter2Page from '../neo_mentorship/neo_m_step2/mentoring2/counter2_page';
import MenteeTasks1 from '../neo_mentorship/neo_m_step1/mentoring1/mentee_tasks1';

// # Import QuranPage component
import QuranPage from '../general/extra_content/take_break/QuranPage';

// # Import UWorld components for each step
import UWorldQBankStep1 from '../neo_mentorship/neo_m_step1/qbanks1/uw1';
import UWorldQBankStep2 from '../neo_mentorship/neo_m_step2/qbanks2/uw2';

// # Import Amboss components for each step
import AmbossQBankStep1 from '../neo_mentorship/neo_m_step1/qbanks1/amboss1';
import AmbossQBankStep2 from '../neo_mentorship/neo_m_step2/qbanks2/amboss2';

// # Import NBME components for each step
import NBMEQBankStep1 from '../neo_mentorship/neo_m_step1/qbanks1/nbme1';
import NBMEQBankStep2 from '../neo_mentorship/neo_m_step2/qbanks2/nbme2';

// # Import Step 1 management components
import Tasks1 from '../neo_mentorship/neo_m_step1/tutora1/tasks1';
import MentorNotes1 from '../neo_mentorship/neo_m_step1/tutora1/mentor_notes1';
import Checklist1 from '../neo_mentorship/neo_m_step1/management1/checklist1';
import Dedicated1 from '../neo_mentorship/neo_m_step1/management1/dedicated1';
import MenteeMeetings1 from '../neo_mentorship/neo_m_step1/management1/mentee_meetings1';
import WeakSpots1 from '../neo_mentorship/neo_m_step1/management1/weakspots1';
import Assignments1 from '../neo_mentorship/neo_m_step1/assignments1/assignments1';
// # Import new Step 1 components
import GiveAssignment1 from '../neo_mentorship/neo_m_step1/assignments1/give_assign1';
import DedicatedTasks1 from '../neo_mentorship/neo_m_step1/tutora1/give_dedic1';

// # Import Step 2 management components
import Tasks2 from '../neo_mentorship/neo_m_step2/tutora2/tasks2';
import MentorNotes2 from '../neo_mentorship/neo_m_step2/tutora2/mentor_notes2';
import Checklist2 from '../neo_mentorship/neo_m_step2/management2/checklist2';
import Dedicated2 from '../neo_mentorship/neo_m_step2/management2/dedicated2';
import MenteeMeetings2 from '../neo_mentorship/neo_m_step2/management2/mentee_meetings2';
import WeakSpots2 from '../neo_mentorship/neo_m_step2/management2/weakspots2';
import Assignments2 from '../neo_mentorship/neo_m_step2/assignments2/assignments2';
// # Import new Step 2 components
import GiveAssignment2 from '../neo_mentorship/neo_m_step2/assignments2/give_assign2';
import DedicatedTasks2 from '../neo_mentorship/neo_m_step2/tutora2/give_dedic2';

// # Import tasks components
import TaskChapter1 from '../neo_mentorship/neo_m_step1/tutora1/task_chapter1';
import TaskChapter2 from '../neo_mentorship/neo_m_step2/tutora2/task_chapter2';

// # Import stats components
import MenteeStats1 from '../neo_mentorship/neo_m_step1/M_stats1/mentee_stats1';
import MenteeStats2 from '../neo_mentorship/neo_m_step2/M_stats2/mentee_stats2';

// # Import meetings components
import AdminMeetingsMain from '../systems/meetings/adm_meet/admin_meet_mn';
import AdminGeneralMeetings from '../systems/meetings/adm_meet/adm_gen_meet';
import AdminStep1Meetings from '../systems/meetings/adm_meet/adm_step1_meet';
import AdminStep2Meetings from '../systems/meetings/adm_meet/adm_step2_meet';
import AdminOETMeetings from '../systems/meetings/adm_meet/adm_oet_meet';
import AdminVisaMeetings from '../systems/meetings/adm_meet/adm_visa_meet';

// # Import MentorDashboard component
import MentorDashboard from '../roles/admin/mentor/mentor';

const AppRoutes = () => {
  // # Get authentication state from the store
  const { isAuthenticated } = useAuthStore();
  
  return (
    <BrowserRouter>
      {/* # Add ScrollToTop component */}
      <ScrollToTop />
      <Routes>
        {/* # Public routes */}
        <Route path="/" element={
          <ProtectedRoute>
            {(authState) => (
              authState ? <Navigate to="/dashboard" replace /> : <Landing />
            )}
          </ProtectedRoute>
        } />
        
        {/* # Include public guide routes */}
        {publicRoutes}

        <Route path="/reviews" element={<Reviews />} />
        <Route path="/signin" element={<Signin />} />
        <Route path="/signup/*" element={<SignupRoutes />} />
        {/* # Add maintenance routes */}
        <Route path="/signin-maintenance" element={<SigninMock />} />
        <Route path="/signup-maintenance" element={<SignupMock />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/products" element={<Products />} />
        {/* <Route path="/team" element={<Team />} /> */}
        <Route path="/samples" element={<Samples />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/faqs" element={<FAQs />} />

        {/* # Mentor Routes */}
        <Route path="/mentor/*" element={
          <ProtectedRouteAdmin>
            <MentorRoutes />
          </ProtectedRouteAdmin>
        } />

        {/* # Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute adminOnly={true}>
            <AdminDashboard />
          </ProtectedRoute>
        } />

        {/* # Employee Role Routes */}
        <Route path="/tutor-dashboard/*" element={
          <ProtectedRoute requiredRole="tutor">
            <TutorRoutes />
          </ProtectedRoute>
        } />
        
        {/* # Direct tutor mentorship route */}
        <Route path="/tutor/*" element={
          <ProtectedRoute requiredRole="tutor">
            <TutorRoutes />
          </ProtectedRoute>
        } />
        
        <Route path="/content-reviewer-dashboard/*" element={
          <ProtectedRoute requiredRole="content_reviewer">
            <ContentReviewerRoutes />
          </ProtectedRoute>
        } />
        
        <Route path="/financial-manager-dashboard/*" element={
          <ProtectedRoute requiredRole="financial_manager">
            <FinancialManagerRoutes />
          </ProtectedRoute>
        } />
        
        <Route path="/designer-dashboard/*" element={
          <ProtectedRoute requiredRole="designer">
            <DesignerRoutes />
          </ProtectedRoute>
        } />
        
        <Route path="/support-dashboard/*" element={
          <ProtectedRoute requiredRole="support">
            <SupportRoutes />
          </ProtectedRoute>
        } />

        {/* # Admin Secured Routes - Must come before general admin routes */}
        <Route path="/admin/analytics/*" element={
            <ProtectedRoute adminOnly={true}>
            <AdminSecuredRoutes />
            </ProtectedRoute>
        } />
        <Route path="/admin/leaderboards" element={
            <ProtectedRoute adminOnly={true}>
            <AdminLeaderboards />
            </ProtectedRoute>
        } />
        <Route path="/admin/users/*" element={
            <ProtectedRoute adminOnly={true}>
            <AdminSecuredRoutes />
            </ProtectedRoute>
        } />
        <Route path="/admin/financial/*" element={
            <ProtectedRoute adminOnly={true}>
            <AdminSecuredRoutes />
            </ProtectedRoute>
        } />
        <Route path="/admin/content/*" element={
            <ProtectedRoute adminOnly={true}>
            <AdminSecuredRoutes />
            </ProtectedRoute>
        } />
        <Route path="/admin/content-creation/*" element={
            <ProtectedRoute adminOnly={true}>
            <AdminSecuredRoutes />
            </ProtectedRoute>
        } />
        <Route path="/admin/subscriptions/*" element={
            <ProtectedRoute adminOnly={true}>
            <AdminSecuredRoutes />
            </ProtectedRoute>
        } />
        <Route path="/admin/feedback/*" element={
            <ProtectedRoute adminOnly={true}>
            <AdminSecuredRoutes />
            </ProtectedRoute>
        } />
        <Route path="/admin/support/*" element={
            <ProtectedRoute adminOnly={true}>
            <AdminSecuredRoutes />
            </ProtectedRoute>
        } />
        <Route path="/admin/review-management/*" element={
            <ProtectedRoute adminOnly={true}>
            <AdminSecuredRoutes />
            </ProtectedRoute>
        } />
        <Route path="/admin/tech-board/*" element={
            <ProtectedRoute adminOnly={true}>
            <AdminSecuredRoutes />
            </ProtectedRoute>
        } />
        <Route path="/admin/mentorship/*" element={
            <ProtectedRoute adminOnly={true}>
            <AdminSecuredRoutes />
            </ProtectedRoute>
        } />

        {/* # Admin Community Routes */}
        <Route path="/admin/community/break/quran" element={
          <ProtectedRoute adminOnly={true}>
            <QuranPage />
          </ProtectedRoute>
        } />

        {/* # Admin Dashboard Routes - For other admin features */}
        <Route path="/admin/*" element={
          <ProtectedRoute adminOnly={true}>
            <AdminDashRoutes />
          </ProtectedRoute>
        } />

        {/* # User Dashboard */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <UserDashboard />
            </ProtectedRoute>
        } />

        {/* # User Profile and Settings */}
        <Route path="/profile" element={
          <ProtectedRoute adminOnly={false}>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/admin/profile" element={
          <ProtectedRoute adminOnly={true}>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/settings/*" element={
          <ProtectedRoute adminOnly={false}>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings/*" element={
          <ProtectedRoute adminOnly={true}>
            <Settings />
          </ProtectedRoute>
        } />

        {/* # General Routes for shared features */}
        <Route path="/*" element={<GeneralRoutes />} />

        {/* # Library Routes - User */}
        <Route path="/library/*" element={
          <Routes>
            <Route path="step1/*" element={
              <ProtectedRouteUser requiredSubscription="step1_library">
                <Routes>
                  <Route path="/" element={<LibraryStep1 />} />
                  <Route path=":chapterId" element={<Step1Chapter />} />
                  <Route path=":chapterId/topic/:topicId" element={<ChapterIframe />} />
                  <Route path="stats" element={<ChapterStats />} />
                </Routes>
              </ProtectedRouteUser>
            } />
            <Route path="step2/*" element={
              <ProtectedRouteUser requiredSubscription="step2_library">
                <Routes>
                  <Route path="/" element={<LibraryStep2 />} />
                  <Route path=":chapterId" element={<Step2Chapter />} />
                  <Route path=":chapterId/topic/:topicId" element={<ChapterIframe />} />
                  <Route path="stats" element={<ChapterStats2 />} />
                </Routes>
              </ProtectedRouteUser>
            } />
          </Routes>
        } />

        {/* # Library Routes - Admin */}
        <Route path="/admin/library/*" element={
          <Routes>
            <Route path="step1/*" element={
              <ProtectedRouteAdmin>
                <Routes>
                  <Route path="/" element={<LibraryStep1 isAdmin={true} />} />
                  <Route path=":chapterId" element={<Step1Chapter />} />
                  <Route path=":chapterId/topic/:topicId" element={<ChapterIframe />} />
                  <Route path="stats" element={<ChapterStats />} />
                </Routes>
              </ProtectedRouteAdmin>
            } />
            <Route path="step2/*" element={
              <ProtectedRouteAdmin>
                <Routes>
                  <Route path="/" element={<LibraryStep2 isAdmin={true} />} />
                  <Route path=":chapterId" element={<Step2Chapter />} />
                  <Route path=":chapterId/topic/:topicId" element={<ChapterIframe />} />
                  <Route path="stats" element={<ChapterStats2 />} />
                </Routes>
              </ProtectedRouteAdmin>
            } />
          </Routes>
        } />

        {/* # User Mentorship Routes */}
        <Route path="/mentorship/*" element={
          <Routes>
            <Route path="step1/*" element={
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <Routes>
                  <Route path="/" element={<NeoMenteeStep1 />} />
                  <Route path="time" element={<NeoTime stepType="Step 1" />} />
                  <Route path="calendar" element={<M1Calendar isAdmin={false} />} />
                  <Route path="stats" element={<MenteeStats1 isAdmin={false} />} />
                  <Route path="mentoring1/counter1" element={<Counter1 isAdmin={false} />} />
                  <Route path="mentoring1/counter1/:chapterId" element={<Counter1Page isAdmin={false} />} />
                  <Route path="uworld" element={<UWorldQBankStep1 isAdmin={false} />} />
                  <Route path="amboss" element={<AmbossQBankStep1 isAdmin={false} />} />
                  <Route path="nbme" element={<NBMEQBankStep1 isAdmin={false} />} />
                  <Route path="tasks" element={<Tasks1 isAdmin={false} />} />
                  <Route path="notes" element={<MentorNotes1 isAdmin={false} />} />
                  <Route path="checklist" element={<Checklist1 isAdmin={false} />} />
                  <Route path="plan" element={<Dedicated1 isAdmin={false} />} />
                  <Route path="meetings" element={<MenteeMeetings1 isAdmin={false} />} />
                  <Route path="weakspots" element={<WeakSpots1 isAdmin={false} />} />
                  <Route path="assignments" element={<Assignments1 isAdmin={false} />} />
                  {/* Add mentee tasks route */}
                  <Route path="mentoring1/tasks" element={<MenteeTasks1 isAdmin={false} />} />
                  {/* Add task chapter route */}
                  <Route path="tutora1/chapter/:chapterId" element={<TaskChapter1 isAdmin={false} />} />
                </Routes>
              </ProtectedRouteUser>
            } />
            <Route path="step2/*" element={
              <ProtectedRouteUser requiredSubscription="step2_mentorship">
                <Routes>
                  <Route path="/" element={<NeoMenteeStep2 />} />
                  <Route path="time" element={<NeoTime stepType="Step 2" />} />
                  <Route path="calendar" element={<M2Calendar isAdmin={false} />} />
                  <Route path="stats" element={<MenteeStats2 isAdmin={false} />} />
                  <Route path="mentoring2/counter2" element={<Counter2 isAdmin={false} />} />
                  <Route path="mentoring2/counter2/:chapterId" element={<Counter2Page isAdmin={false} />} />
                  <Route path="uworld" element={<UWorldQBankStep2 isAdmin={false} />} />
                  <Route path="amboss" element={<AmbossQBankStep2 isAdmin={false} />} />
                  <Route path="nbme" element={<NBMEQBankStep2 isAdmin={false} />} />
                  <Route path="tasks" element={<Tasks2 isAdmin={false} />} />
                  <Route path="notes" element={<MentorNotes2 isAdmin={false} />} />
                  <Route path="checklist" element={<Checklist2 isAdmin={false} />} />
                  <Route path="plan" element={<Dedicated2 isAdmin={false} />} />
                  <Route path="meetings" element={<MenteeMeetings2 isAdmin={false} />} />
                  <Route path="weakspots" element={<WeakSpots2 isAdmin={false} />} />
                  <Route path="assignments" element={<Assignments2 isAdmin={false} />} />
                  {/* # Add new routes for Step 2 */}
                  <Route path="give-assignment" element={<GiveAssignment2 isAdmin={false} />} />
                  <Route path="dedicated-tasks" element={<DedicatedTasks2 isAdmin={false} />} />
                </Routes>
              </ProtectedRouteUser>
            } />
            <Route path="step1/mentee-guide" element={
              <ProtectedRouteUser requiredSubscription="step1_mentorship">
                <MenteeGuide isAdmin={false} stepType="1" />
              </ProtectedRouteUser>
            } />
            <Route path="step2/mentee-guide" element={
              <ProtectedRouteUser requiredSubscription="step2_mentorship">
                <MenteeGuide isAdmin={false} stepType="2" />
              </ProtectedRouteUser>
            } />
          </Routes>
        } />

        {/* # User Mentorship Routes */}
        <Route path="/mentorship/step1/tutora1/tasks" element={
          <ProtectedRouteUser requiredSubscription="step1_mentorship">
            <Tasks1 isAdmin={false} />
          </ProtectedRouteUser>
        } />
        
        <Route path="/mentorship/step1/tutora1/chapter/:chapterId" element={
          <ProtectedRouteUser requiredSubscription="step1_mentorship">
            <TaskChapter1 isAdmin={false} />
          </ProtectedRouteUser>
        } />

        {/* # Remove old admin route and add new mentor-style route */}
        <Route path="/mentor/step1/:username/tutora1/chapter/:chapterId" element={
          <ProtectedRouteAdmin>
            <TaskChapter1 isAdmin={true} isMentorView={true} />
          </ProtectedRouteAdmin>
        } />

        {/* # Add tutor route with consistent pattern */}
        <Route path="/tutor/mentorship/step1/:username/tutora1/chapter/:chapterId" element={
          <ProtectedRoute requiredRole="tutor">
            <TaskChapter1 isAdmin={false} isTutor={true} isMentorView={true} />
          </ProtectedRoute>
        } />

        {/* # Add Step 2 chapter route */}
        <Route path="/mentorship/step2/tutora2/chapter/:chapterId" element={
          <ProtectedRouteUser requiredSubscription="step2_mentorship">
            <TaskChapter2 isAdmin={false} />
          </ProtectedRouteUser>
        } />

        {/* # Admin Mentorship Routes */}
        <Route path="/admin/mentorship/step1/tutora1/chapter/:chapterId" element={
          <ProtectedRouteAdmin>
            <TaskChapter1 isAdmin={true} />
          </ProtectedRouteAdmin>
        } />

        {/* # Add Admin Step 2 chapter route */}
        <Route path="/admin/mentorship/step2/tutora2/chapter/:chapterId" element={
          <ProtectedRouteAdmin>
            <TaskChapter2 isAdmin={true} />
          </ProtectedRouteAdmin>
        } />

        {/* # Admin Meetings Routes */}
        <Route path="/admin/meetings" element={
            <ProtectedRouteAdmin>
              <AdminMeetingsMain />
            </ProtectedRouteAdmin>
        } />
        <Route path="/admin/meetings/general" element={
            <ProtectedRouteAdmin>
              <AdminGeneralMeetings />
            </ProtectedRouteAdmin>
        } />
        <Route path="/admin/meetings/step1" element={
            <ProtectedRouteAdmin>
              <AdminStep1Meetings />
            </ProtectedRouteAdmin>
        } />
        <Route path="/admin/meetings/step2" element={
            <ProtectedRouteAdmin>
              <AdminStep2Meetings />
            </ProtectedRouteAdmin>
        } />
        <Route path="/admin/meetings/oet" element={
            <ProtectedRouteAdmin>
              <AdminOETMeetings />
            </ProtectedRouteAdmin>
        } />
        <Route path="/admin/meetings/visa" element={
            <ProtectedRouteAdmin>
              <AdminVisaMeetings />
            </ProtectedRouteAdmin>
        } />

        {/* # Catch-all route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;
